import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import * as XLSX from 'xlsx';
import { 
  Upload, 
  FileSpreadsheet, 
  AlertCircle, 
  CheckCircle, 
  X, 
  Download,
  Loader2,
  Trash2,
  AlertTriangle
} from 'lucide-react';
import { ProductsService } from '../../services/products/products.service';
import { NotificationToast } from '../ui/NotificationToast';

interface ProductRow {
  nombre: string;
  precio: number;
  precioCompra?: number;
  stock: number;
  categoria: string;
  descripcion?: string;
  receta?: boolean;
  fechaVencimiento?: string;
  registroInvima?: string;
  valid: boolean;
  errors: string[];
}

interface BulkUploadDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const BulkUploadDrawer: React.FC<BulkUploadDrawerProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [parsedData, setParsedData] = useState<ProductRow[]>([]);
  const [step, setStep] = useState<'upload' | 'preview' | 'success'>('upload');
  const [showNotification, setShowNotification] = useState(false);
  const [notificationConfig, setNotificationConfig] = useState<{
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
  } | null>(null);

  // Handle animation
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => setIsVisible(true), 10);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
      setTimeout(() => {
        setStep('upload');
        setParsedData([]);
        setUploadProgress(0);
        setIsLoading(false);
      }, 300);
    }
  }, [isOpen]);



  const categoriasValidas = [
    'medicamentos', 'cuidado-personal', 'higiene-femenina', 'salud-sexual',
    'vitaminas-y-suplementos', 'bebes', 'equipos-medicos'
  ];

  const validarFila = (row: any): ProductRow => {
    const errors: string[] = [];
    
    if (!row.nombre || typeof row.nombre !== 'string' || row.nombre.trim().length < 2) {
      errors.push('Nombre inválido (mínimo 2 caracteres)');
    }
    
    if (row.precio === undefined || row.precio === null || isNaN(row.precio) || Number(row.precio) < 1) {
      errors.push('Precio de venta inválido (debe ser mayor a 0)');
    }
    
    if (row.stock === undefined || row.stock === null || isNaN(row.stock) || Number(row.stock) < 0) {
      errors.push('Stock inválido (no puede ser negativo)');
    }
    
    const categoria = String(row.categoria || '').toLowerCase().trim();
    if (!categoriasValidas.includes(categoria)) {
      errors.push(`Categoría inválida. Opciones: ${categoriasValidas.join(', ')}`);
    }

    return {
      nombre: row.nombre || '',
      precio: Number(row.precio) || 0,
      precioCompra: row.precioCompra ? Number(row.precioCompra) : undefined,
      stock: Number(row.stock) || 0,
      categoria: categoria,
      descripcion: row.descripcion || '',
      receta: row.receta === true || row.receta === 'true' || row.receta === 'Sí' || row.receta === 'Si' || row.receta === 1,
      fechaVencimiento: row.fechaVencimiento || '',
      registroInvima: row.registroInvima || '',
      valid: errors.length === 0,
      errors
    };
  };

  const parseFile = async (file: File) => {
    const extension = file.name.split('.').pop()?.toLowerCase();
    let data: any[] = [];

    try {
      if (extension === 'csv') {
        const text = await file.text();
        const lines = text.split('\n').filter(l => l.trim());
        const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/"/g, ''));
        
        data = lines.slice(1).map(line => {
          const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
          const row: any = {};
          headers.forEach((h, i) => {
            row[h] = values[i];
          });
          return row;
        });
      } else {
        const buffer = await file.arrayBuffer();
        const workbook = XLSX.read(buffer);
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        data = XLSX.utils.sheet_to_json(worksheet);
      }

      const validated = data.map(validarFila);
      setParsedData(validated);
      setStep('preview');
    } catch (error) {
      setNotificationConfig({
        title: 'Error al procesar archivo',
        message: 'No se pudo leer el archivo. Verifica el formato.',
        type: 'error'
      });
      setShowNotification(true);
    }
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length > 0) {
      parseFile(acceptedFiles[0]);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls']
    },
    maxFiles: 1
  });

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const downloadTemplate = () => {
    const template = [
      { nombre: 'Paracetamol 500mg', precioCompra: 5000, precio: 8500, stock: 100, categoria: 'medicamentos', descripcion: 'Analgésico y antipirético', receta: false, fechaVencimiento: '2026-12-31', registroInvima: '2023M-001234' },
      { nombre: 'Vitamina C 1000mg', precioCompra: 8000, precio: 12000, stock: 50, categoria: 'vitaminas-y-suplementos', descripcion: 'Suplemento vitamínico', receta: false, fechaVencimiento: '', registroInvima: '' },
      { nombre: 'Pañales Talla M', precioCompra: 30000, precio: 45000, stock: 30, categoria: 'bebes', descripcion: 'Paquete de 40 unidades', receta: false, fechaVencimiento: '', registroInvima: '' },
    ];
    
    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Plantilla');
    XLSX.writeFile(wb, 'plantilla_productos.xlsx');
  };

  const handleUpload = async () => {
    const validRows = parsedData.filter(row => row.valid);
    
    if (validRows.length === 0) {
      setNotificationConfig({
        title: 'Sin productos válidos',
        message: 'No hay productos válidos para importar. Corrige los errores.',
        type: 'error'
      });
      setShowNotification(true);
      return;
    }

    setIsLoading(true);
    setUploadProgress(0);

    const total = validRows.length;
    let completed = 0;
    let errors = 0;

    for (const row of validRows) {
      try {
        await ProductsService.create({
          nombre: row.nombre,
          descripcion: row.descripcion,
          precio: row.precio,
          stock: row.stock,
          categoria: row.categoria as any,
          requiereReceta: row.receta || false,
          precioCompra: row.precioCompra,
          fechaVencimiento: row.fechaVencimiento ? new Date(row.fechaVencimiento) : undefined,
          registroInvima: row.registroInvima,
        } as any);
        completed++;
      } catch (error) {
        errors++;
        console.error('Error creating product:', row.nombre, error);
      }
      setUploadProgress(Math.round(((completed + errors) / total) * 100));
    }

    setIsLoading(false);
    setStep('success');
    
    setNotificationConfig({
      title: '¡Importación completada!',
      message: `${completed} productos importados${errors > 0 ? `, ${errors} errores` : ''}`,
      type: errors > 0 ? 'warning' : 'success'
    });
    setShowNotification(true);

    if (completed > 0) {
      setTimeout(() => {
        handleClose();
        onSuccess?.();
      }, 2000);
    }
  };

  const removeRow = (index: number) => {
    setParsedData(prev => prev.filter((_, i) => i !== index));
  };

  const validRowsCount = parsedData.filter(r => r.valid).length;
  const invalidRowsCount = parsedData.filter(r => !r.valid).length;

  if (!isOpen) return null;

  return (
    <>
      <div 
        className={`fixed inset-0 bg-black/50 z-40 transition-opacity duration-300 ${
          isVisible ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={handleClose}
      />
      
      <div 
        className={`fixed right-0 top-20 h-[calc(100vh-5rem)] w-full max-w-2xl bg-white shadow-2xl z-40 transform transition-transform duration-300 ease-in-out ${
          isVisible ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileSpreadsheet className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Carga Masiva de Productos
                </h2>
                <p className="text-sm text-gray-500">
                  Importa productos desde Excel o CSV
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {step === 'upload' && (
              <div className="space-y-6">
                {/* Template download */}
                <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                  <div className="flex items-start gap-3">
                    <Download className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900">Descargar plantilla</h3>
                      <p className="text-sm text-gray-600 mb-3">
                        Descarga la plantilla con el formato correcto para importar productos.
                      </p>
                      <button
                        onClick={downloadTemplate}
                        className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        Descargar Excel
                      </button>
                    </div>
                  </div>
                </div>

                {/* Dropzone */}
                <div
                  {...getRootProps()}
                  className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                    isDragActive 
                      ? 'border-emerald-500 bg-emerald-50' 
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <input {...getInputProps()} />
                  <Upload className={`w-12 h-12 mx-auto mb-4 ${isDragActive ? 'text-emerald-500' : 'text-gray-400'}`} />
                  <p className="text-lg font-medium text-gray-900 mb-1">
                    {isDragActive ? 'Suelta el archivo aquí' : 'Arrastra tu archivo aquí'}
                  </p>
                  <p className="text-sm text-gray-500">
                    o haz clic para seleccionar archivo
                  </p>
                  <p className="text-xs text-gray-400 mt-2">
                    Formatos soportados: .xlsx, .xls, .csv
                  </p>
                </div>

                {/* Instructions */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-500" />
                    Requisitos del archivo
                  </h4>
                  <ul className="text-sm text-gray-600 space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-500 mt-1">•</span>
                      <span>Columnas requeridas: <strong>nombre, precio, stock, categoria</strong></span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-500 mt-1">•</span>
                      <span>Columnas opcionales: <strong>precioCompra, descripcion, receta, fechaVencimiento, registroInvima</strong></span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-emerald-500 mt-1">•</span>
                      <span>Categorías válidas: medicamentos, cuidado-personal, vitaminas, higiene, bebes, equipos</span>
                    </li>
                  </ul>
                </div>
              </div>
            )}

            {step === 'preview' && (
              <div className="space-y-4">
                {/* Stats */}
                <div className="flex gap-4">
                  <div className="flex-1 p-4 bg-emerald-50 rounded-xl border border-emerald-200">
                    <p className="text-2xl font-bold text-emerald-600">{validRowsCount}</p>
                    <p className="text-sm text-gray-600">Productos válidos</p>
                  </div>
                  <div className="flex-1 p-4 bg-red-50 rounded-xl border border-red-200">
                    <p className="text-2xl font-bold text-red-600">{invalidRowsCount}</p>
                    <p className="text-sm text-gray-600">Con errores</p>
                  </div>
                </div>

                {/* Table */}
                <div className="border rounded-xl overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-gray-600 font-medium">Estado</th>
                        <th className="px-4 py-3 text-left text-gray-600 font-medium">Nombre</th>
                        <th className="px-4 py-3 text-left text-gray-600 font-medium">Costo</th>
                        <th className="px-4 py-3 text-left text-gray-600 font-medium">Precio Venta</th>
                        <th className="px-4 py-3 text-left text-gray-600 font-medium">Stock</th>
                        <th className="px-4 py-3 text-left text-gray-600 font-medium">Categoría</th>
                        <th className="px-4 py-3 text-center text-gray-600 font-medium">Acciones</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {parsedData.map((row, index) => (
                        <tr key={index} className={row.valid ? '' : 'bg-red-50/50'}>
                          <td className="px-4 py-3">
                            {row.valid ? (
                              <CheckCircle className="w-5 h-5 text-emerald-500" />
                            ) : (
                              <div className="group relative">
                                <AlertTriangle className="w-5 h-5 text-red-500" />
                                <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-48 p-2 bg-red-100 text-red-800 text-xs rounded shadow-lg z-10">
                                  {row.errors.join(', ')}
                                </div>
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-gray-900">{row.nombre || '-'}</td>
                          <td className="px-4 py-3 text-gray-600">${row.precioCompra || '-'}</td>
                          <td className="px-4 py-3 text-gray-600">${row.precio}</td>
                          <td className="px-4 py-3 text-gray-600">{row.stock}</td>
                          <td className="px-4 py-3 text-gray-600">{row.categoria}</td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => removeRow(index)}
                              className="p-1.5 hover:bg-red-100 rounded-lg text-red-500 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {step === 'success' && (
              <div className="flex flex-col items-center justify-center h-full text-center p-8">
                <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mb-6">
                  <CheckCircle className="w-10 h-10 text-emerald-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  ¡Importación completada!
                </h3>
                <p className="text-gray-600">
                  Los productos han sido importados exitosamente.
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          {step !== 'success' && (
            <div className="px-6 py-4 border-t bg-gray-50">
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={step === 'preview' ? () => setStep('upload') : handleClose}
                  className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  {step === 'preview' ? 'Volver' : 'Cancelar'}
                </button>
                {step === 'preview' && (
                  <button
                    onClick={handleUpload}
                    disabled={isLoading || validRowsCount === 0}
                    className="flex-1 px-4 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        {uploadProgress}%
                      </>
                    ) : (
                      <>
                        <Upload className="w-5 h-5" />
                        Importar {validRowsCount} productos
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {showNotification && notificationConfig && (
        <NotificationToast
          isOpen={showNotification}
          title={notificationConfig.title}
          message={notificationConfig.message}
          type={notificationConfig.type}
          onClose={() => setShowNotification(false)}
        />
      )}
    </>
  );
};

export default BulkUploadDrawer;
